import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Clapperboard, Loader2, Copy, Film, Wand2 } from "lucide-react";
import { api } from "../api";

const TERMINAL = ["completed", "failed"];

export function VideoStudio() {
  const [opts, setOpts] = useState({ avatars: [], voices: [], courses: [], default_avatar: "", default_voice: "" });
  const [title, setTitle] = useState("");
  const [script, setScript] = useState("");
  const [avatarId, setAvatarId] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [vslParts, setVslParts] = useState([]);
  const [vslIdx, setVslIdx] = useState(0);
  const [vslCourse, setVslCourse] = useState("nlp_practitioner");
  const pollers = useRef({});

  const loadVideos = useCallback(async () => {
    try { const r = await api.get("/owner/heygen/videos"); setVideos(r.data); } catch {}
  }, []);

  useEffect(() => {
    api.get("/owner/heygen/options").then((r) => {
      setOpts(r.data);
      setAvatarId(r.data.default_avatar || "");
      setVoiceId(r.data.default_voice || "");
    }).catch(() => setOpts({ avatars: [], voices: [], courses: [] }));
    loadVideos();
  }, [loadVideos]);

  const poll = useCallback((id) => {
    if (pollers.current[id]) return;
    pollers.current[id] = setInterval(async () => {
      try {
        const r = await api.get(`/owner/heygen/videos/${id}`);
        setVideos((prev) => prev.map((v) => (v.id === id ? r.data : v)));
        if (TERMINAL.includes(r.data.status)) { clearInterval(pollers.current[id]); delete pollers.current[id]; }
      } catch {}
    }, 8000);
  }, []);

  useEffect(() => {
    videos.forEach((v) => { if (!TERMINAL.includes(v.status)) poll(v.id); });
    return () => { Object.values(pollers.current).forEach(clearInterval); pollers.current = {}; };
  }, [videos, poll]);

  const generate = async (e) => {
    e.preventDefault();
    if (!title.trim() || !script.trim()) { toast.error("Add a title and a script."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/owner/heygen/generate", {
        title, script, avatar_id: avatarId || null, voice_id: voiceId || null, aspect_ratio: "16:9",
      });
      setVideos((p) => [data, ...p]);
      setTitle(""); setScript("");
      toast.success("Video is rendering — it'll appear below in a minute or two.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the video.");
    } finally { setBusy(false); }
  };

  const assign = async (course_id, video_url) => {
    try { await api.post("/owner/heygen/assign", { course_id, video_url }); toast.success(video_url ? "Added to the sales page." : "Removed from the sales page."); }
    catch { toast.error("Could not update the sales page."); }
  };

  const copy = (url) => { navigator.clipboard?.writeText(url); toast("Link copied"); };

  const fillPart = (parts, idx, courseTitle) => {
    const multi = parts.length > 1;
    setTitle(`${courseTitle} — VSL${multi ? ` (Part ${idx + 1} of ${parts.length})` : ""}`);
    setScript(parts[idx]);
    setVslIdx(idx);
  };

  const loadVsl = async () => {
    try {
      const { data } = await api.get(`/owner/heygen/script/${vslCourse}`);
      setVslParts(data.parts);
      fillPart(data.parts, 0, data.title);
      toast.success(data.parts.length > 1
        ? `Loaded Part 1 of ${data.parts.length} — generate it, then load the next part.`
        : "Full VSL script loaded — ready to generate.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load that script.");
    }
  };

  return (
    <section className="hub-card" id="video-studio" data-testid="video-studio" style={{ gridColumn: "1 / -1" }}>
      <h3><Clapperboard size={18} /> Video Studio <span className="cnt">HeyGen</span></h3>
      <p className="empty" style={{ marginTop: 0 }}>
        Turn a script into an AI avatar video with your Evan avatar &amp; voice, then show it on a sales page. Max 5,000 characters per video — split long VSLs into parts.
      </p>

      <form onSubmit={generate} style={{ display: "grid", gap: ".6rem", marginTop: ".8rem" }}>
        <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", alignItems: "center", padding: ".6rem .7rem", background: "var(--panel)", borderRadius: 10, border: "1px dashed var(--line)" }}>
          <Wand2 size={16} style={{ color: "#f0c869" }} />
          <span style={{ fontSize: ".82rem", color: "#cabfae" }}>One-click VSL:</span>
          <select data-testid="vs-vsl-course" value={vslCourse} onChange={(e) => setVslCourse(e.target.value)}
            style={{ padding: ".4rem", borderRadius: 8, border: "1px solid var(--line)", background: "var(--bg)", color: "#fff" }}>
            {(opts.courses.length ? opts.courses : [{ id: "nlp_practitioner", title: "NLP Practitioner" }]).map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
          <button type="button" className="btn btn-gold-outline" style={{ fontSize: ".8rem" }} data-testid="vs-load-vsl" onClick={loadVsl}>
            Load VSL script
          </button>
          {vslParts.length > 1 && vslIdx < vslParts.length - 1 && (
            <button type="button" className="btn btn-soft" style={{ fontSize: ".8rem" }} data-testid="vs-next-part"
              onClick={() => fillPart(vslParts, vslIdx + 1, title.replace(/ — VSL.*/, ""))}>
              Load Part {vslIdx + 2}
            </button>
          )}
        </div>
        <input data-testid="vs-title" placeholder="Video title (e.g. NLP Practitioner — VSL Part 1)"
          value={title} onChange={(e) => setTitle(e.target.value)}
          style={{ padding: ".6rem", borderRadius: 8, border: "1px solid var(--line)", background: "var(--panel)", color: "#fff" }} />
        <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap" }}>
          <label style={{ flex: "1 1 220px", fontSize: ".8rem", color: "#b9ad9c" }}>Avatar
            <select data-testid="vs-avatar" value={avatarId} onChange={(e) => setAvatarId(e.target.value)}
              style={{ width: "100%", padding: ".5rem", borderRadius: 8, border: "1px solid var(--line)", background: "var(--panel)", color: "#fff", marginTop: 4 }}>
              {opts.default_avatar && !opts.avatars.some((a) => a.id === opts.default_avatar) &&
                <option value={opts.default_avatar}>Evan (default)</option>}
              {opts.avatars.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
            </select>
          </label>
          <label style={{ flex: "1 1 220px", fontSize: ".8rem", color: "#b9ad9c" }}>Voice
            <select data-testid="vs-voice" value={voiceId} onChange={(e) => setVoiceId(e.target.value)}
              style={{ width: "100%", padding: ".5rem", borderRadius: 8, border: "1px solid var(--line)", background: "var(--panel)", color: "#fff", marginTop: 4 }}>
              {opts.default_voice && !opts.voices.some((v) => v.id === opts.default_voice) &&
                <option value={opts.default_voice}>Evan (default)</option>}
              {opts.voices.map((v) => <option key={v.id} value={v.id}>{v.name}{v.gender ? ` · ${v.gender}` : ""}</option>)}
            </select>
          </label>
        </div>
        <textarea data-testid="vs-script" rows={6} maxLength={5000} placeholder="Paste the script the avatar should speak…"
          value={script} onChange={(e) => setScript(e.target.value)}
          style={{ padding: ".7rem", borderRadius: 8, border: "1px solid var(--line)", background: "var(--panel)", color: "#fff" }} />
        <div style={{ display: "flex", alignItems: "center", gap: ".8rem" }}>
          <small className="empty">{script.length}/5000</small>
          <button className="btn btn-gold" type="submit" disabled={busy} data-testid="vs-generate" style={{ marginLeft: "auto" }}>
            {busy ? <><Loader2 size={16} className="spin" /> Starting…</> : <><Film size={16} /> Generate video</>}
          </button>
        </div>
      </form>

      <div style={{ marginTop: "1.2rem", display: "grid", gap: ".8rem" }} data-testid="vs-list">
        {videos.length === 0 && <p className="empty">Your generated videos will appear here.</p>}
        {videos.map((v) => (
          <div key={v.id} className="hrow" style={{ display: "block" }} data-testid={`vs-item-${v.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".5rem" }}>
              <strong>{v.title}</strong>
              <span className="pill" data-testid={`vs-status-${v.id}`}>
                {!TERMINAL.includes(v.status) && <Loader2 size={12} className="spin" style={{ marginRight: 4 }} />}
                {v.status}
              </span>
            </div>
            {v.status === "completed" && v.video_url && (
              <>
                <video src={v.video_url} controls style={{ width: "100%", maxWidth: 520, marginTop: ".6rem", borderRadius: 10 }} />
                <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", marginTop: ".5rem", alignItems: "center" }}>
                  <button className="btn btn-soft" style={{ fontSize: ".8rem" }} onClick={() => copy(v.video_url)}><Copy size={14} /> Copy link</button>
                  {opts.courses.map((c) => (
                    <button key={c.id} className="btn btn-gold-outline" style={{ fontSize: ".8rem" }}
                      data-testid={`vs-assign-${v.id}-${c.id}`} onClick={() => assign(c.id, v.video_url)}>
                      Show on {c.title}
                    </button>
                  ))}
                </div>
              </>
            )}
            {v.status === "failed" && <p className="empty" style={{ color: "#e08" }}>Render failed — try a shorter script or a different avatar.</p>}
          </div>
        ))}
      </div>

      {opts.courses.length > 0 && (
        <div style={{ marginTop: "1rem" }}>
          <span className="pill">Remove a video from a sales page</span>
          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", marginTop: ".5rem" }}>
            {opts.courses.map((c) => (
              <button key={c.id} className="btn btn-soft" style={{ fontSize: ".8rem" }}
                data-testid={`vs-clear-${c.id}`} onClick={() => assign(c.id, null)}>Clear {c.title}</button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
